/** Keep the draft and verification with the assignments so the backend can reproduce a release. */
import {
  levelsForScope,
  type FinalizeResult,
  type LengthUnit,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "../domain/studioTypes";
import type { RuleSet } from "../domain/storeRules";
import { emitReplicaRequest } from "../domain/finalize";
import { compressedStoreRules, compressedRulesDisagreement } from "../domain/compressedStoreRules";
import {
  CdtRuleSetSchema,
  type PostMerchflowCdtCompression,
} from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { CDT_DRAFT_SCHEMA_VERSION } from "../store/cdtCompressionService";
import {
  coverStoreRange,
  type StoreRangeCoverage,
  type StoreRangeProduct,
} from "../domain/coverStoreRange";

const CDT0_ATTR = "__cdt0";

/** CDT0 has no priority column, so its measured order is the only order record. */
export const shippedCdt0Order = (result: FinalizeResult): readonly string[] => [
  ...(result.contract.valueOrder[CDT0_ATTR] ?? []),
];

export const toCompressionPayload = (
  result: FinalizeResult,
  draft: StudioDraft,
  basePogId: number | null,
  ruleSet: RuleSet,
): PostMerchflowCdtCompression => ({
  schemaVersion: CDT_DRAFT_SCHEMA_VERSION,
  basePogId,
  assignments: result.assignments.map((assignment) => ({
    productCode: assignment.skuId,
    cdt0: assignment.cdt0,
    cdt1: assignment.cdt1,
    cdt2: assignment.cdt2,
    cdt3: assignment.cdt3,
    cdt1Priority: assignment.cdt1Priority,
    cdt2Priority: assignment.cdt2Priority,
    cdt3Priority: assignment.cdt3Priority,
  })),
  cdt0Order: [...shippedCdt0Order(result)],
  draft,
  contract: result.contract,
  verification: result.verification,
  ruleSet: CdtRuleSetSchema.parse(ruleSet),
  // ALWAYS false here: `shipCdtCompression` sets it, and only for a retry the user asked for.
  acknowledgeUnplacedCombos: false,
});

interface PrepareCompressionPayloadInput {
  result: FinalizeResult;
  draft: StudioDraft;
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  basePogId: number | null;
  /** DEV-3694: the category's products the base does not carry; each ships next to its
   *  closest match (`coverStoreRange`). */
  storeRange?: readonly StoreRangeProduct[];
  /** The category's unit, for the shipped advisories' words only. */
  lengthUnit?: LengthUnit;
}

/** Shipping is allowed only when one coherent draft produces both the tree and store rules. */
export const prepareCompressionPayloadWithCoverage = ({
  result: baseResult,
  draft,
  items,
  runs,
  basePogId,
  storeRange = [],
  lengthUnit,
}: PrepareCompressionPayloadInput): {
  payload: PostMerchflowCdtCompression;
  coverage: StoreRangeCoverage;
} => {
  const rootAttr = levelsForScope(draft.spine, draft.scopedLevelOrder, [])[0]?.attr;
  if (!rootAttr) throw new Error("The CDT Studio tree has no root attribute");
  const cdt0Order = shippedCdt0Order(baseResult);
  const baseMeasured = emitReplicaRequest(items, runs, {
    sectionAttr: rootAttr,
    draft,
    cdt0Order,
    lengthUnit,
  }).rules;
  const { result, measured, coverage } = coverStoreRange(
    baseResult,
    baseMeasured,
    items,
    draft,
    storeRange,
  );
  const ruleSet = compressedStoreRules(measured, { assignments: result.assignments, cdt0Order });
  const disagreement = compressedRulesDisagreement(ruleSet, {
    assignments: result.assignments,
    cdt0Order,
  });
  if (disagreement !== null) throw new Error(disagreement);
  return { payload: toCompressionPayload(result, draft, basePogId, ruleSet), coverage };
};

export const prepareCompressionPayload = (
  input: PrepareCompressionPayloadInput,
): PostMerchflowCdtCompression => prepareCompressionPayloadWithCoverage(input).payload;
